import { useState } from "react";
import { ClipboardList, Info, Layers, Tag } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { useText } from "@/lib/useText";
import { VideoBackdrop } from "@/components/site/VideoBackdrop";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function EmptyPrices({ t }) {
  return (
    <div className="flex min-h-[240px] flex-col justify-center rounded-md border border-dashed border-eden-border bg-eden-surface/60 p-8 sm:p-10" data-testid="pricing-empty-state">
      <ClipboardList size={26} strokeWidth={1.5} className="mb-6 text-eden-turquoise" aria-hidden="true" />
      <h3 className="font-display text-lg font-semibold text-eden-text">{t("pricing_empty_title")}</h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-eden-secondary">{t("pricing_empty_text")}</p>
    </div>
  );
}

function PriceList({ category, t }) {
  if (!category.items.length) return <EmptyPrices t={t} />;
  return (
    <div className="overflow-hidden rounded-md border border-eden-border bg-eden-surface" data-testid={`pricing-list-${category.id}`}>
      <div className="flex justify-between gap-4 border-b border-eden-border px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-eden-muted sm:px-7" aria-hidden="true">
        <span>{t("pricing_item_label")}</span><span>{t("pricing_value_label")}</span>
      </div>
      <dl className="divide-y divide-eden-borderSubtle">
        {category.items.map((item) => (
          <div key={item.id} className="grid gap-3 px-5 py-6 transition-colors hover:bg-eden-elevated sm:grid-cols-[minmax(0,1fr)_minmax(100px,40%)] sm:gap-8 sm:px-7" data-testid={`pricing-item-${category.id}-${item.id}`}>
            <dt className="min-w-0 break-words">
              <span className="font-display text-base font-semibold text-eden-text" data-testid={`pricing-name-${category.id}-${item.id}`}>{item.name}</span>
              {item.description && <span className="mt-1.5 block whitespace-pre-line text-sm leading-relaxed text-eden-secondary" data-testid={`pricing-description-${category.id}-${item.id}`}>{item.description}</span>}
            </dt>
            <dd className={`min-w-0 self-center break-words font-mono text-sm sm:text-right ${item.price ? "text-eden-turquoise" : "text-eden-muted"}`} data-testid={`pricing-price-${category.id}-${item.id}`}>
              {item.price || t("pricing_pending")}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function PricingDirectory({ pricing, t }) {
  const [selected, setSelected] = useState("");
  const categories = pricing.categories;
  const active = categories.some((c) => c.id === selected) ? selected : categories[0]?.id;
  if (!categories.length) return <EmptyPrices t={t} />;
  return (
    <Tabs value={active} onValueChange={setSelected} data-testid="pricing-directory">
      <div className="overflow-x-auto pb-2" data-testid="pricing-tabs-scroll">
        <TabsList className="h-auto min-w-full justify-start gap-2 rounded-none bg-transparent p-0" aria-label={t("pricing_categories_label")} data-testid="pricing-tabs">
          {categories.map((category) => (
            <TabsTrigger key={category.id} value={category.id} className="min-h-12 gap-5 rounded-sm border border-eden-border bg-eden-surface px-5 py-3 text-eden-secondary transition-colors hover:border-eden-orange/50 hover:text-eden-text focus-visible:ring-eden-orange data-[state=active]:border-eden-orange data-[state=active]:bg-eden-orange data-[state=active]:text-eden-bg data-[state=active]:shadow-none" data-testid={`pricing-tab-${category.id}`}>
              <span className="max-w-[260px] truncate">{category.title}</span>
              <span className="font-mono text-xs opacity-70" data-testid={`pricing-count-${category.id}`}>{String(category.items.length).padStart(2, "0")}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      {categories.map((category) => (
        <TabsContent key={category.id} value={category.id} className="mt-10 focus-visible:ring-eden-orange" data-testid={`pricing-panel-${category.id}`}>
          <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-14">
            <div className="min-w-0 border-l-2 border-eden-orange pl-5 lg:self-start">
              <h2 className="break-words text-base font-semibold text-eden-text md:text-lg" data-testid={`pricing-category-title-${category.id}`}>{category.title}</h2>
              {category.description && <p className="mt-3 whitespace-pre-line break-words text-sm leading-relaxed text-eden-secondary" data-testid={`pricing-category-description-${category.id}`}>{category.description}</p>}
            </div>
            <PriceList category={category} t={t} />
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
}

export default function Precario() {
  const { content } = useContent();
  const t = useText();
  const pricing = content.pricing;
  return (
    <div data-testid="pricing-page">
      <section className="relative overflow-hidden border-b border-eden-border py-16 sm:py-20" aria-labelledby="pricing-title" data-testid="pricing-hero">
        <VideoBackdrop opacity={0.18} />
        <div className="container-eden relative">
          <p className="eyebrow flex items-center gap-2" data-testid="pricing-eyebrow"><Tag size={14} aria-hidden="true" />{pricing.eyebrow}</p>
          <h1 id="pricing-title" className="mt-5 max-w-4xl break-words text-4xl font-bold tracking-tight text-eden-text sm:text-5xl lg:text-6xl" data-testid="pricing-title">{pricing.title}</h1>
          <p className="mt-6 max-w-2xl whitespace-pre-line break-words text-sm leading-relaxed text-eden-secondary sm:text-base" data-testid="pricing-intro">{pricing.text}</p>
        </div>
      </section>
      <section className="container-eden py-10 sm:py-14" aria-label={t("pricing_categories_label")}>
        <p className="mb-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-eden-muted" data-testid="pricing-categories-label"><Layers size={13} aria-hidden="true" />{t("pricing_categories_label")}</p>
        <PricingDirectory pricing={pricing} t={t} />
        {pricing.note && <p className="mt-10 flex items-start gap-3 border-t border-eden-border pt-6 text-sm text-eden-secondary" data-testid="pricing-note"><Info size={16} className="mt-0.5 shrink-0 text-eden-turquoise" aria-hidden="true" /><span className="min-w-0 whitespace-pre-line break-words">{pricing.note}</span></p>}
      </section>
    </div>
  );
}
